import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function PaintersSimonstownPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isScrolled, setIsScrolled] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'Simons Town Painters | Professional Painting Services | painterssimonstown.co.za',
    description: 'Expert painting services in Simons Town with 18+ years experience. Residential, commercial painting, interior and exterior. Free quotes, quality workmanship, competitive rates. Serving Simons Town and False Bay area.',
    keywords: 'painters Simons Town, painting services Simons Town, house painters Simons Town, commercial painters Simons Town, residential painting Simons Town',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Simons Town',
          description: 'Professional painting services in Simons Town with over 18 years of experience',
          address: 'Simons Town, Western Cape',
          priceRange: '$$',
          serviceArea: ['Simons Town', 'Fish Hoek', 'Kalk Bay', 'St James', 'Muizenberg']
        }),
        generateServiceSchema({
          name: 'Coastal Painting Services',
          description: 'Residential and commercial painting services for coastal properties',
          provider: 'Painters Simons Town',
          areaServed: ['Simons Town', 'Fish Hoek', 'False Bay'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Waterproofing']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Navigation */}
      <nav className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-paint-brush-line text-2xl text-[#e9f638]"></i>
              <span className="text-xl font-bold">Cape Town Painters</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Get a Quote</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/4b863eb77dd3d9c9c0f10abb6f6bdd51.jpg"
            alt="Painters Simons Town - Professional Painting Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 w-full">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Painters Simons Town</h1>
          <p className="text-xl md:text-2xl mb-6">Expert Painting Services in Simons Town & False Bay</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="http://painterssimonstown.co.za" target="_blank" rel="noopener noreferrer" className="bg-[#e9f638] text-black px-8 py-3 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Visit Website</a>
            <Link to="/get-a-quote" className="bg-white text-black px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap font-semibold">Get Free Quote</Link>
          </div>
        </div>
      </div>

      {/* Quick Info Bar */}
      <div className="bg-[#e9f638] py-6">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-black">18+</div>
              <div className="text-black font-medium">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">3200+</div>
              <div className="text-black font-medium">Projects Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">99%</div>
              <div className="text-black font-medium">Client Satisfaction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">Local</div>
              <div className="text-black font-medium">Simons Town Experts</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b sticky top-[72px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="container mx-auto px-4 py-12">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">About Painters Simons Town</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters Simons Town</strong> has been the premier choice for professional painting services in Simons Town and the False Bay area for over 18 years. Our deep understanding of the unique coastal environment, combined with our commitment to quality craftsmanship and customer satisfaction, has established us as the trusted painting partner for homeowners and businesses throughout this historic naval town and surrounding communities.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Our Heritage and Local Expertise</h3>
              <p className="text-lg leading-relaxed mb-6">
                Founded in 2006, <strong>Painters Simons Town</strong> was established by local residents who understood the specific challenges and requirements of painting in a coastal environment. Over the past 18 years, we have developed specialized expertise in protecting and beautifying properties against the harsh marine conditions that characterize the False Bay coastline. Our experience includes working with historic naval buildings, charming Victorian homes, modern coastal residences, and commercial properties throughout Simons Town.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                The coastal location of Simons Town presents unique challenges for painting projects. Salt spray, strong winds, intense sun exposure, and high humidity levels require specialized knowledge, premium materials, and proven techniques to ensure long-lasting, beautiful results. Our team has mastered the art of coastal painting, understanding which products perform best in marine environments, how to properly prepare surfaces exposed to salt air, and how to apply coatings that withstand the elements while maintaining their aesthetic appeal.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Comprehensive Painting Services</h3>
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters Simons Town</strong> offers a complete range of painting services tailored to meet the needs of our coastal community. Our residential painting services include interior painting, exterior painting, roof painting and waterproofing, deck and balcony coating, fence painting and staining, damp proofing and treatment, and color consultation. We understand that your home is your sanctuary, and we work diligently to enhance its beauty while protecting it from the coastal elements.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                For commercial clients, we provide professional painting solutions that maintain property values, enhance curb appeal, and create positive impressions on customers and visitors. Our commercial services include retail space painting, restaurant and hospitality painting, office painting, building exterior painting, and specialized coatings for marine-related businesses. We understand the importance of minimizing disruption to business operations and work efficiently to complete projects on schedule.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Specialized Coastal Protection</h3>
              <p className="text-lg leading-relaxed mb-6">
                One of our core specializations is protecting properties from the damaging effects of coastal weather conditions. <strong>Painters Simons Town</strong> uses premium marine-grade paints and coatings specifically formulated to resist salt corrosion, UV damage, moisture penetration, and wind-driven rain. Our surface preparation process is thorough and comprehensive, addressing any existing damage, treating rust and corrosion, sealing cracks and gaps, and applying appropriate primers before finishing coats.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                We pay special attention to areas most vulnerable to coastal damage, including exterior woodwork, metal fixtures and railings, roof surfaces, balconies and decks, and window frames. Our protective coating systems are designed to provide maximum durability while maintaining the aesthetic character of your property. We also offer specialized waterproofing services to protect against water infiltration, which is particularly important in the high-rainfall winter months experienced in the False Bay area.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Quality Materials for Coastal Conditions</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters Simons Town</strong>, we understand that using the right materials is essential for achieving long-lasting results in coastal environments. We partner with leading paint manufacturers to source premium products specifically designed for marine and coastal applications. Our product selection includes high-performance exterior paints with enhanced UV resistance, marine-grade primers and sealers, specialized rust-inhibiting coatings, waterproofing membranes and sealants, and eco-friendly low-VOC options for interior applications.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                We take the time to educate our clients about the different product options available and help them select the best solutions for their specific needs and budget. Our expertise in coastal painting means we can recommend products that will perform optimally in Simons Town's unique climate, providing superior protection and longevity compared to standard painting products.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Our Proven Process</h3>
              <p className="text-lg leading-relaxed mb-6">
                Every project at <strong>Painters Simons Town</strong> follows a systematic process designed to ensure exceptional results. We begin with a comprehensive site assessment where we evaluate the condition of surfaces, identify any damage or deterioration, assess environmental exposure factors, and discuss your vision and requirements. This thorough evaluation allows us to develop an accurate quote and realistic timeline for your project.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Our preparation phase is extensive and includes power washing to remove salt deposits and contaminants, scraping and sanding to remove loose or failing paint, repairing damaged wood, stucco, or other surfaces, treating rust and corrosion on metal surfaces, filling cracks and gaps, applying specialized primers, and protecting surrounding areas and landscaping. We never rush the preparation phase because we know it's the foundation of a successful painting project.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                During the painting phase, our experienced craftsmen apply coatings using proven techniques that ensure even coverage, proper film thickness, and professional finishes. We monitor weather conditions carefully and adjust our schedule as needed to ensure optimal application conditions. Our attention to detail extends to every aspect of the work, from cutting in around trim and fixtures to achieving smooth, consistent finishes throughout the project.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Service Areas in the False Bay Region</h3>
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters Simons Town</strong> proudly serves Simons Town and the entire False Bay area. Our service coverage includes Simons Town, Fish Hoek, Kalk Bay, St James, Muizenberg, Glencairn, Scarborough, Kommetjie, Ocean View, Noordhoek, Sun Valley, Clovelly, and surrounding coastal communities. Our local presence means we can respond quickly to inquiries, provide prompt service, and offer ongoing support to our clients throughout the False Bay region.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Environmental Responsibility</h3>
              <p className="text-lg leading-relaxed mb-6">
                As a company operating in one of South Africa's most beautiful coastal areas, <strong>Painters Simons Town</strong> takes environmental responsibility seriously. We are committed to sustainable painting practices that protect the marine environment and minimize our ecological footprint. Our environmental initiatives include using eco-friendly paints with low or zero VOC content, proper disposal of paint waste and materials, protecting marine life during coastal projects, minimizing water usage and runoff, and recycling paint containers and materials whenever possible.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Customer Satisfaction and Guarantees</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters Simons Town</strong>, your complete satisfaction is our top priority. We stand behind the quality of our work with comprehensive warranties and guarantees. Our 99% customer satisfaction rating reflects our commitment to excellence and our dedication to exceeding client expectations on every project. We maintain open communication throughout the project, keeping you informed of progress, addressing any concerns promptly, and ensuring the final result meets or exceeds your expectations.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                We understand that inviting contractors into your home or business requires trust. That's why we maintain the highest standards of professionalism, reliability, and integrity in all our interactions. Our team members are courteous, respectful, and committed to providing a positive experience from initial consultation through project completion. We treat every property as if it were our own, taking care to protect your belongings and maintain a clean, safe work environment.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Get Started with Your Painting Project</h3>
              <p className="text-lg leading-relaxed mb-6">
                Ready to protect and beautify your Simons Town property with professional painting services? Contact <strong>Painters Simons Town</strong> today for a free, no-obligation quote. Our friendly team is ready to discuss your project, answer your questions, and provide expert guidance on all your painting needs. With over 18 years of coastal painting experience, thousands of satisfied customers, and a commitment to excellence, we're the painting partner you can trust to deliver outstanding results that withstand the test of time and the elements.
              </p>

              <div className="bg-[#e9f638] p-8 rounded-lg mt-8">
                <h4 className="text-2xl font-bold mb-4">Contact Information</h4>
                <div className="space-y-3">
                  <p className="flex items-center text-lg">
                    <i className="ri-global-line text-2xl mr-3"></i>
                    <a href="http://painterssimonstown.co.za" target="_blank" rel="noopener noreferrer" className="hover:underline font-medium">painterssimonstown.co.za</a>
                  </p>
                  <p className="flex items-center text-lg">
                    <i className="ri-map-pin-line text-2xl mr-3"></i>
                    <span className="font-medium">Serving Simons Town & False Bay area</span>
                  </p>
                  <p className="flex items-center text-lg">
                    <i className="ri-time-line text-2xl mr-3"></i>
                    <span className="font-medium">Monday - Saturday: 7:30 AM - 5:30 PM</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Painting Services</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=coastal%20home%20interior%20painting%20fresh%20painted%20walls%20ocean%20view%20elegant%20furniture%20natural%20lighting%20professional%20finish%20simple%20background&width=400&height=300&seq=service-residential-st&orientation=landscape"
                    alt="Residential Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Residential Painting</h3>
                  <p className="text-gray-700 mb-4">Complete interior and exterior painting for coastal homes, using marine-grade products for lasting protection.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Interior wall painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Exterior house painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Coastal protection coating</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Deck and balcony painting</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=commercial%20building%20painting%20Simons%20Town%20business%20property%20professional%20painters%20working%20clean%20organized%20simple%20background&width=400&height=300&seq=service-commercial-st&orientation=landscape"
                    alt="Commercial Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Commercial Painting</h3>
                  <p className="text-gray-700 mb-4">Professional painting for businesses and commercial properties with minimal disruption to operations.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Retail space painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Restaurant painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Office painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Building exteriors</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=waterproofing%20roof%20coating%20protection%20coastal%20building%20marine%20environment%20professional%20application%20simple%20background&width=400&height=300&seq=service-waterproof-st&orientation=landscape"
                    alt="Waterproofing Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Waterproofing</h3>
                  <p className="text-gray-700 mb-4">Specialized waterproofing services to protect your property from coastal moisture and weather.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Roof waterproofing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Wall damp proofing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Balcony waterproofing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Basement sealing</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=exterior%20building%20painting%20coastal%20property%20marine%20grade%20paint%20professional%20finish%20ocean%20view%20simple%20background&width=400&height=300&seq=service-exterior-st&orientation=landscape"
                    alt="Exterior Painting"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Exterior Painting</h3>
                  <p className="text-gray-700 mb-4">Marine-grade exterior painting designed to withstand harsh coastal conditions and salt spray.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Building facades</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Roof painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Fence and gate painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Rust treatment</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=interior%20room%20painting%20elegant%20coastal%20home%20fresh%20colors%20professional%20finish%20comfortable%20atmosphere%20simple%20background&width=400&height=300&seq=service-interior-st&orientation=landscape"
                    alt="Interior Painting"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Interior Painting</h3>
                  <p className="text-gray-700 mb-4">Beautiful interior painting services using premium paints for lasting beauty and durability.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Wall painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Ceiling painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Trim and molding</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Feature walls</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=color%20consultation%20paint%20samples%20swatches%20coastal%20interior%20design%20planning%20professional%20advice%20simple%20background&width=400&height=300&seq=service-consultation-st&orientation=landscape"
                    alt="Color Consultation"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Color Consultation</h3>
                  <p className="text-gray-700 mb-4">Expert color consultation to help you choose perfect colors that complement coastal living.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Color scheme design</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Sample testing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Coastal color palettes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Trend guidance</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Work Gallery</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautifully%20painted%20coastal%20home%20interior%20Simons%20Town%20ocean%20view%20fresh%20walls%20elegant%20furniture%20natural%20lighting%20simple%20background&width=400&height=500&seq=gallery-st-1&orientation=portrait"
                  alt="Coastal Home Interior Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautiful%20house%20exterior%20freshly%20painted%20Simons%20Town%20coastal%20property%20ocean%20view%20professional%20finish%20simple%20background&width=400&height=500&seq=gallery-st-2&orientation=portrait"
                  alt="Exterior House Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=commercial%20building%20painted%20Simons%20Town%20business%20property%20professional%20finish%20coastal%20setting%20simple%20background&width=400&height=500&seq=gallery-st-3&orientation=portrait"
                  alt="Commercial Property Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=elegant%20bedroom%20interior%20painting%20coastal%20home%20soft%20colors%20professional%20finish%20comfortable%20atmosphere%20simple%20background&width=400&height=500&seq=gallery-st-4&orientation=portrait"
                  alt="Bedroom Painting Project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=waterproofed%20roof%20coating%20coastal%20building%20marine%20protection%20professional%20application%20simple%20background&width=400&height=500&seq=gallery-st-5&orientation=portrait"
                  alt="Roof Waterproofing Project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=painted%20deck%20balcony%20coastal%20home%20ocean%20view%20marine%20grade%20coating%20professional%20finish%20simple%20background&width=400&height=500&seq=gallery-st-6&orientation=portrait"
                  alt="Deck and Balcony Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Client Reviews</h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Painters Simons Town did an exceptional job on our coastal home. They understood the challenges of painting in a marine environment and used the right products to ensure lasting protection. The team was professional, punctual, and the results are stunning!"</p>
                <p className="font-semibold">- Catherine M., Simons Town</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"We hired Painters Simons Town for our restaurant in Fish Hoek. They worked efficiently around our operating hours and delivered beautiful results. Their knowledge of coastal painting is impressive. Highly recommend!"</p>
                <p className="font-semibold">- David R., Fish Hoek</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Excellent service from Painters Simons Town! They painted the exterior of our home in Kalk Bay and the transformation is amazing. The marine-grade paint they used has held up perfectly against the salt spray. Very professional team!"</p>
                <p className="font-semibold">- Susan T., Kalk Bay</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Painters Simons Town provided outstanding service for our waterproofing and painting project. Their expertise in coastal protection is evident in the quality of their work. The team was friendly, reliable, and delivered exactly what they promised!"</p>
                <p className="font-semibold">- Andrew L., Glencairn</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"We're thrilled with the interior painting work done by Painters Simons Town. Their color consultation service was invaluable, and the painters were meticulous in their work. Our home looks beautiful! Best painting company in the False Bay area!"</p>
                <p className="font-semibold">- Michelle P., St James</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-black text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Protect Your Coastal Property?</h2>
          <p className="text-xl mb-8">Get a free quote from Painters Simons Town today</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-8 py-3 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Get Free Quote</Link>
            <a href="http://painterssimonstown.co.za" target="_blank" rel="noopener noreferrer" className="bg-white text-black px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap font-semibold">Visit Website</a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#e9f638] text-black py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Cape Town Painters</h3>
              <p className="text-sm">Your trusted directory for professional painting services across South Africa.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:underline">Home</Link></li>
                <li><Link to="/about" className="hover:underline">About</Link></li>
                <li><Link to="/contact" className="hover:underline">Contact</Link></li>
                <li><Link to="/get-a-quote" className="hover:underline">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                <li>Residential Painting</li>
                <li>Commercial Painting</li>
                <li>Waterproofing</li>
                <li>Coastal Protection</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Cape Town, South Africa
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  info@painters-cape-town.co.za
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-black/20 pt-8 text-center text-sm">
            <p>&copy; 2024 Cape Town Painters Directory. All rights reserved.</p>
            <div className="mt-2 space-x-4">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">Powered by leaderr</a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">SEO by SEO Studio</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
